import { Alert, Button, Divider, FormControlLabel, Grid, Switch, TextField, Tooltip } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { withStyles } from "@mui/styles";
import { useEffect, useState } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import { Styles } from "src/styles/material-styles";
import { NotificationVariant } from "src/utils/enums/notification-variants.enum";
import * as LLMChatbotSettingsService from "src/services/llm-chatbot-settings.service";
import { normalizeLLMChatbotSettings } from "src/services/llm-chatbot-settings.service";
import CopilotTemplates from "../copilot-templates";

// Item 09 (16/10/2026) — sub-aba "Configuração de chatbot": libera o copiloto de IA no editor de fluxo (JOINT).
const ADDITIONAL_INSTRUCTIONS_MAX_LENGTH = 4000;

const defaultItem = {
    flowBuilderEnabled: false,
    additionalInstructions: ""
};

const ChatbotConfiguration = (props) => {
    const { intl, llm, onShowNotification, onLoading } = props;

    const [item, setItem] = useState(defaultItem);

    const fetchSettings = async () => {
        onLoading(true);
        try {
            const output = await LLMChatbotSettingsService.getByLLMId(llm.id);
            if (output?.data) {
                setItem(normalizeLLMChatbotSettings(output.data));
            }
        } catch (err) {
            onShowNotification(intl.formatMessage({ id: "process.error" }), NotificationVariant.ERROR);
        }
        onLoading(false);
    };

    useEffect(() => {
        if (llm?.id) {
            fetchSettings();
        }
    }, [llm]);

    const handleEnabledChange = (e) => {
        const checked = e.target.checked;
        setItem((prev) => ({ ...prev, flowBuilderEnabled: checked }));
    };

    const handleInstructionsChange = (e) => {
        const value = e.target.value ?? "";
        setItem((prev) => ({ ...prev, additionalInstructions: value.slice(0, ADDITIONAL_INSTRUCTIONS_MAX_LENGTH) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        onLoading(true);
        try {
            const result = await LLMChatbotSettingsService.update(llm.id, {
                flowBuilderEnabled: item.flowBuilderEnabled,
                additionalInstructions: item.additionalInstructions
            });
            if (result && result.success === false) {
                const message = result.errors?.[0] ?? intl.formatMessage({ id: "process.error" });
                onShowNotification(message, NotificationVariant.ERROR);
            } else {
                onShowNotification(intl.formatMessage({ id: "llm.chatbot.settings.saved" }));
            }
        } catch (err) {
            const message = err?.response?.data?.errors?.[0] ?? intl.formatMessage({ id: "process.error" });
            onShowNotification(message, NotificationVariant.ERROR);
        }
        onLoading(false);
    };

    return (
        <>
        <form className={props.classes.form} onSubmit={handleSubmit}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={item.flowBuilderEnabled}
                                onChange={handleEnabledChange}
                                color="primary"
                            />
                        }
                        label={
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                {intl.formatMessage({ id: "llm.chatbot.settings.flow.builder.enabled" })}
                                <Tooltip title={intl.formatMessage({ id: "llm.chatbot.settings.flow.builder.enabled.help" })} arrow>
                                    <HelpOutlineIcon fontSize="small" style={{ color: "#9e9e9e", cursor: "help" }} />
                                </Tooltip>
                            </span>
                        }
                    />
                </Grid>

                {item.flowBuilderEnabled && (
                    <>
                        <Grid item xs={12}>
                            <Alert severity="info">
                                <FormattedMessage id="llm.chatbot.settings.flow.builder.info" />
                            </Alert>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                multiline
                                rows={5}
                                fullWidth
                                variant="outlined"
                                name="additionalInstructions"
                                label={intl.formatMessage({ id: "llm.chatbot.settings.additional.instructions" })}
                                placeholder={intl.formatMessage({ id: "llm.chatbot.settings.additional.instructions.placeholder" })}
                                value={item.additionalInstructions}
                                onChange={handleInstructionsChange}
                                helperText={`${intl.formatMessage({ id: "llm.chatbot.settings.additional.instructions.helper" })} (${item.additionalInstructions.length}/${ADDITIONAL_INSTRUCTIONS_MAX_LENGTH})`}
                            />
                        </Grid>
                    </>
                )}

                <Grid item xs={12} sm={4}>
                    <Button type="submit" fullWidth variant="contained" color="primary">
                        <FormattedMessage id="save" />
                    </Button>
                </Grid>
            </Grid>
        </form>

        {/* Item 09b (16/10/2026): templates do copiloto — valem pra todas as LLMs do contratante. */}
        <Divider sx={{ mt: 3 }} />
        <CopilotTemplates onShowNotification={onShowNotification} onLoading={onLoading} />
        </>
    );
};

export default injectIntl(withStyles(Styles)(ChatbotConfiguration));
